// Companion → Voice engines (plan §6.5): which voice engine each call uses,
// and with which settings. Four tabs: Rollout (the Remote Config
// `voice_engine` policy), Profiles (engine settings, Postgres), Test users
// (per-user overrides) and Results (per-arm analytics, read-only).
//
// Every Studio admin can view the page. Writes need the uid in
// VOICE_ENGINE_ADMIN_UIDS, which fails closed on prod: without it the page
// is read-only, and a write refused with 403 turns it read-only too.

import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { getProfiles, getVoiceEngines, type Overview, type ProfilesResponse } from "../services/voiceEngines";
import { Callout } from "../components/voiceEngines/parts";
import { errorText } from "../components/voiceEngines/errors";
import { RolloutTab } from "../components/voiceEngines/RolloutTab";
import { ProfilesTab } from "../components/voiceEngines/ProfilesTab";
import { TestUsersTab } from "../components/voiceEngines/TestUsersTab";
import { ResultsTab } from "../components/voiceEngines/ResultsTab";

type Tab = "rollout" | "profiles" | "test-users" | "results";

const TABS: { key: Tab; label: string }[] = [
  { key: "rollout", label: "Rollout" },
  { key: "profiles", label: "Profiles" },
  { key: "test-users", label: "Test users" },
  { key: "results", label: "Results" },
];

function isTab(value: string | null): value is Tab {
  return TABS.some((t) => t.key === value);
}

export function VoiceEnginesPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const wanted = searchParams.get("tab");
  const tab: Tab = isTab(wanted) ? wanted : "rollout";
  // Tabs stay mounted once opened, so a half-edited draft survives a switch.
  const [opened, setOpened] = useState<Set<Tab>>(() => new Set([tab]));

  const [overview, setOverview] = useState<Overview | null>(null);
  const [profiles, setProfiles] = useState<ProfilesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [profilesError, setProfilesError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState<string | null>(null);

  const loadOverview = useCallback(async () => {
    try {
      setOverview(await getVoiceEngines());
      setError(null);
    } catch (e) {
      setError(errorText(e));
    }
  }, []);

  const loadProfiles = useCallback(async () => {
    try {
      setProfiles(await getProfiles());
      setProfilesError(null);
    } catch (e) {
      setProfilesError(errorText(e));
    }
  }, []);

  useEffect(() => {
    loadOverview();
    loadProfiles();
  }, [loadOverview, loadProfiles]);

  const select = (t: Tab) => {
    setOpened((s) => new Set(s).add(t));
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (t === "rollout") next.delete("tab");
        else next.set("tab", t);
        return next;
      },
      { replace: true },
    );
  };

  const canWrite = !!overview?.viewer.canWrite && forbidden === null;
  const environment = overview?.env.environment ?? "";

  return (
    <div className="list-page ve-page">
      <div className="ve-head">
        <h1>Voice engines</h1>
        <span className="chip mono">voice_engine</span>
        {environment && <span className="chip">{environment}</span>}
      </div>
      <p className="muted ve-intro">
        Which voice engine each call uses, and with which settings. Servers re-read the Remote Config value every 60
        seconds, profiles and test users every 30.
      </p>

      {overview && !canWrite && (
        <Callout tone="warn" badge="View only">
          {forbidden ??
            `Changing voice engines on ${environment} needs your uid in VOICE_ENGINE_ADMIN_UIDS.`}{" "}
          {overview.viewer.writeGate === "closed" &&
            "The list is empty here, so nobody can change them until it is set. "}
          You can still look around, preview drafts and look users up.
        </Callout>
      )}

      <div className="tabs ve-tabs" role="tablist" aria-label="Voice engines">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            className={tab === t.key ? "active" : ""}
            onClick={() => select(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {(error && !overview) || (profilesError && !profiles) ? (
        <div className="state-box error">Voice engines unavailable ({!overview ? error : profilesError}).</div>
      ) : !overview || !profiles ? (
        <div className="state-box">Loading voice engines…</div>
      ) : (
        <>
          {(error || profilesError) && <p className="error-text">{[error, profilesError].filter(Boolean).join(" ")}</p>}
          {(opened.has("rollout") || tab === "rollout") && (
            <div role="tabpanel" hidden={tab !== "rollout"}>
              <RolloutTab
                overview={overview}
                profiles={profiles.profiles}
                canWrite={canWrite}
                userEmail={user?.email ?? null}
                onForbidden={setForbidden}
                onPublished={async () => {
                  await Promise.all([loadOverview(), loadProfiles()]);
                }}
              />
            </div>
          )}
          {(opened.has("profiles") || tab === "profiles") && (
            <div role="tabpanel" hidden={tab !== "profiles"}>
              <ProfilesTab
                data={profiles}
                canWrite={canWrite}
                environment={environment}
                onForbidden={setForbidden}
                onChanged={loadProfiles}
              />
            </div>
          )}
          {(opened.has("test-users") || tab === "test-users") && (
            <div role="tabpanel" hidden={tab !== "test-users"}>
              <TestUsersTab
                profiles={profiles.profiles}
                engines={profiles.engines}
                canWrite={canWrite}
                environment={environment}
                onForbidden={setForbidden}
                onChanged={loadProfiles}
              />
            </div>
          )}
          {(opened.has("results") || tab === "results") && (
            <div role="tabpanel" hidden={tab !== "results"}>
              <ResultsTab overview={overview} />
            </div>
          )}
        </>
      )}
    </div>
  );
}
